const PESEL_CHECKSUM_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];

// Maps encoded month ranges to century base years.
// PESEL encodes the century by adding an offset to the month (e.g. month 21-32 → year 2000s).
const PESEL_CENTURY_OFFSETS = [
  { monthMin: 81, monthMax: 92, centuryBase: 1800 },
  { monthMin: 1, monthMax: 12, centuryBase: 1900 },
  { monthMin: 21, monthMax: 32, centuryBase: 2000 },
  { monthMin: 41, monthMax: 52, centuryBase: 2100 },
  { monthMin: 61, monthMax: 72, centuryBase: 2200 },
];

/**
 * Validates a Polish national identification number (PESEL).
 *
 * The PESEL number is an 11-digit number that encodes the date of birth, gender,
 * and a control digit. This function checks if the provided PESEL is valid
 * by verifying its format and calculating the control digit.
 *
 * @param pesel - The PESEL number to validate
 * @returns `true` if the PESEL is valid or missing, `false` otherwise
 */
export function validatePESEL(pesel: string): boolean {
  if (!pesel) {
    return true;
  }

  return [validateLength, validateChecksum, validateDate].every(validate => validate(pesel));
}

function validateLength(pesel: string): boolean {
  if (!/^\d{11}$/.test(pesel)) {
    return false;
  }

  return true;
}

function validateChecksum(pesel: string): boolean {
  let sum = 0;

  for (let i = 0; i < 10; i++) {
    const digit = parseInt(pesel[i], 10);
    const product = digit * PESEL_CHECKSUM_WEIGHTS[i];

    sum += product % 10;
  }

  const controlDigit = (10 - (sum % 10)) % 10;
  const lastDigit = parseInt(pesel[10], 10);

  return controlDigit === lastDigit;
}

function validateDate(pesel: string): boolean {
  const year = parseInt(pesel.slice(0, 2), 10);
  const encodedMonth = parseInt(pesel.slice(2, 4), 10);
  const day = parseInt(pesel.slice(4, 6), 10);

  const centuryEntry = PESEL_CENTURY_OFFSETS.find(
    ({ monthMin, monthMax }) => encodedMonth >= monthMin && encodedMonth <= monthMax,
  );

  if (!centuryEntry) {
    return false;
  }

  const fullYear = centuryEntry.centuryBase + year;
  const month = encodedMonth - (centuryEntry.monthMin - 1); // decode to 1-based calendar month

  // Date constructor overflows invalid days (e.g. Feb 30 → Mar 1-2),
  // so we verify the components round-trip to catch non-existent dates.
  const date = new Date(fullYear, month - 1, day);

  return date.getFullYear() === fullYear && date.getMonth() === month - 1 && date.getDate() === day;
}
